/**
 * "Thiết lập ban đầu" — what the household has declared so far (plan step 1.9, spec v2 §27).
 *
 * Counts, not stored progress flags. The question "have I set up my accounts?" is already
 * answered by the accounts table; a second copy of that answer in `user_preferences` would be
 * one more thing that can disagree with reality — and it would go stale the moment the owner
 * archives an account outside this screen.
 */
import { LIABILITY_ACCOUNT_TYPES } from '@pfo/domain';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type SetupProgress = Readonly<{
  /** Cash / bank / e-wallet / savings / broker / other — where money sits. */
  assetAccounts: number;
  /** Accounts that ARE a debt (decision B5). */
  liabilityAccounts: number;
  /** Accounts whose starting balance has been declared (spec 8.4, DBD-006). */
  accountsWithOpening: number;
  incomeSources: number;
  recurringTemplates: number;
  reservePolicies: number;
  /** Batches posted by `pfo legacy-import` (tools/pfo-cli). */
  legacyImports: number;
}>;

const LIABILITY_LIST = [...LIABILITY_ACCOUNT_TYPES];

export async function fetchSetupProgress(): Promise<SetupProgress> {
  const [assets, liabilities, sources, templates, policies, batches, openings] = await Promise.all([
    supabase
      .from('accounts')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .not('account_type', 'in', `(${LIABILITY_LIST.join(',')})`),
    supabase
      .from('accounts')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .in('account_type', LIABILITY_LIST),
    supabase
      .from('income_sources')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase
      .from('recurring_templates')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase
      .from('reserve_policies')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true),
    supabase
      .from('import_batches')
      .select('id', { count: 'exact', head: true })
      .eq('source_type', 'legacy_excel'),
    // "Has a starting balance" is a fact about the LEDGER, not about the account row: the
    // opening balance is an entry (DBD-006). Read the account ids an opening entry touched.
    supabase
      .from('v_money_flow_lines')
      .select('account_id')
      .eq('entry_type', 'opening')
      .not('account_id', 'is', null),
  ]);

  for (const result of [assets, liabilities, sources, templates, policies, batches, openings]) {
    if (result.error) throw appErrorException(result.error);
  }

  return {
    assetAccounts: assets.count ?? 0,
    liabilityAccounts: liabilities.count ?? 0,
    accountsWithOpening: new Set((openings.data ?? []).map((row) => row.account_id)).size,
    incomeSources: sources.count ?? 0,
    recurringTemplates: templates.count ?? 0,
    reservePolicies: policies.count ?? 0,
    legacyImports: batches.count ?? 0,
  };
}
